import React, { createContext, useContext, useMemo } from 'react';
import en from './en';

// English-only. The dictionary/lookup shape is kept so existing `t('a.b.c')`
// call sites across the app don't need to change.
function lookup(obj: any, path: string) {
  return path.split('.').reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

type Ctx = {
  locale: 'en';
  setLocale: (l: 'en') => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

function translate(key: string, vars?: Record<string, string | number>) {
  let str = lookup(en, key);
  if (typeof str !== 'string') return key; // fall back to the key if missing
  if (vars) {
    for (const k of Object.keys(vars)) str = str.replace(new RegExp(`{${k}}`, 'g'), String(vars[k]));
  }
  return str;
}

const I18nContext = createContext<Ctx>({
  locale: 'en',
  setLocale: () => {},
  t: translate,
});

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useMemo<Ctx>(() => ({ locale: 'en', setLocale: () => {}, t: translate }), []);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

// Kept for call sites using the standalone `t` import instead of `useI18n().t`.
export const t = translate;
